import React, { useState, useEffect, useCallback, useRef } from "react";
import { Repeat, Calculator } from "lucide-react";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useLoader } from "../../context/loadercontext";
import NoFound from "../common/ui/NoFound";
import { useAuth } from "../../context/AuthContext";
import { createPortal } from "react-dom";
import { Edit2, MoreVertical } from "lucide-react";
import Modal from "../common/forms/modal";
import LabelInput from "../common/forms/Labelinput";

const ActionDropdown = ({ onAdjustment, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const handleToggle = () => {
    if (disabled) return; // prevent opening if disabled

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 120; // width of dropdown
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - dropdownWidth,
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`px-2 py-1 rounded ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'}`}
        disabled={disabled}
      >
        <MoreVertical className="w-4 h-4 text-gray-600" />
      </button>

      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

            <div
              className="absolute w-36 bg-white rounded-md shadow-lg border z-50"
              style={{ top: coords.top, left: coords.left }}
            >
              <button
                onClick={() => {
                  onAdjustment();
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm hover:bg-sidebarHover"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Adjustment
              </button>
            </div>
          </>,
          document.body
        )}
    </>
  );
};


export default function Viewdutyspace() {
  const [activeTab, setActiveTab] = useState('all');
  const [hoveredBIGroup, setHoveredBIGroup] = useState(null);
  const [records, setRecords] = useState([]);
  const { setLoading } = useLoader();
  const { logout } = useAuth();
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState(null);
  const [adjustmentMap, setAdjustmentMap] = useState({});

  const balanceSheetValue = 20000000; // ₹2 Cr

  const getTabColor = () => {
    if (activeTab === 'pending') return 'bg-[#fff1d6] text-[#be7600]';
    if (activeTab === 'clear') return 'bg-[#dbf6e5] text-[#588d57]';
    return 'bg-gray-900 text-white';
  };

  const perBISummary = records.reduce((acc, curr) => {
    if (!acc[curr.biNumber]) acc[curr.biNumber] = 0;
    acc[curr.biNumber] += curr.duty;
    return acc;
  }, {});

  const allBINumbers = Object.keys(perBISummary);
  const clearBINumbers = allBINumbers.filter(bi => perBISummary[bi] === 0);
  const pendingBINumbers = allBINumbers.filter(bi => perBISummary[bi] !== 0);

  const tabs = [
    { id: 'all', label: 'All', count: allBINumbers.length },
    { id: 'clear', label: 'Clear', count: clearBINumbers.length },
    { id: 'pending', label: 'Pending', count: pendingBINumbers.length },
  ];

  const fetchDutySpaceTotals = useCallback(async () => {
    setLoading(true);
    const startTime = Date.now();

    try {
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DUTY_SPACE.GET_ALL_DUTY_SPACE_TOTAL}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // 🔐 Handle token expiry
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          logout();
          return;
        }

        response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DUTY_SPACE.GET_ALL_DUTY_SPACE_TOTAL}`,
          { method: "GET", headers: getHeaders(true) }
        );
      }

      const result = await response.json();

      if (result.status === 200 && Array.isArray(result.data)) {
        const mappedRecords = [];
        const adjustmentObj = {};

        result.data.forEach(item => {
          const { id, bi_number, inbond, exbond, adjustment } = item;

          if (inbond?.total_duty_inbond) {
            mappedRecords.push({
              id,
              biNumber: bi_number,
              type: "Inbond",
              duty: inbond.total_duty_inbond,
            });
          }

          if (exbond?.total_duty_exbond) {
            mappedRecords.push({
              id,
              biNumber: bi_number,
              type: "Exbond",
              duty: -exbond.total_duty_exbond,
            });
          }

          // Adjustment only for table column
          adjustmentObj[bi_number] = adjustment?.total_duty_adjustment ?? 0;
        });

        setRecords(mappedRecords);
        setAdjustmentMap(adjustmentObj);
      } else {
        setRecords([]);
      }
    } catch (error) {
      console.error("Error fetching duty totals:", error);
      setRecords([]);
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, 1000 - elapsed);

      setTimeout(() => {
        setLoading(false);
      }, remainingDelay);
    }
  }, [logout, setLoading]);


  useEffect(() => {
    fetchDutySpaceTotals();
  }, [fetchDutySpaceTotals]);

  // const totalAdjustments = Object.values(perBISummary).reduce((sum, val) => sum + val, 0);
  const totalAdjustments = Object.keys(perBISummary).reduce(
    (sum, biNumber) =>
      sum +
      (perBISummary[biNumber] || 0) +
      (adjustmentMap[biNumber] || 0),
    0
  );

  let currentBalance = balanceSheetValue;

  // Group records by BI number
  const recordsByBI = {};
  records.forEach(item => {
    if (!recordsByBI[item.biNumber]) recordsByBI[item.biNumber] = [];
    recordsByBI[item.biNumber].push(item);
  });

  // Subtract Inbond, add Exbond, apply adjustment per BI
  Object.entries(recordsByBI).forEach(([biNumber, items]) => {
    let inbondTotal = 0;
    let exbondTotal = 0;

    items.forEach(item => {
      if (item.type === "Inbond") inbondTotal += item.duty;
      if (item.type === "Exbond") exbondTotal += Math.abs(item.duty); // duty stored negative, so take abs
    });

    currentBalance = currentBalance - inbondTotal + exbondTotal;

    const adjustment = adjustmentMap[biNumber] || 0;
    if (adjustment >= 0) {
      currentBalance += adjustment;
    } else {
      currentBalance += adjustment; // still add absolute value if negative
    }
  });

  const finalBalance = currentBalance;


  let filteredRecords = records;
  if (activeTab === 'clear') filteredRecords = records.filter(r => clearBINumbers.includes(r.biNumber));
  else if (activeTab === 'pending') filteredRecords = records.filter(r => pendingBINumbers.includes(r.biNumber));

  const groupedByBI = {};
  filteredRecords.forEach((record) => {
    if (!groupedByBI[record.biNumber]) groupedByBI[record.biNumber] = [];
    groupedByBI[record.biNumber].push(record);
  });

  const groupedRecords = [];
  let srNo = 0;
  Object.entries(groupedByBI).forEach(([biNumber, items]) => {
    srNo++;
    const adjustmentValue = adjustmentMap[biNumber] || 0;
    items.forEach((item, idx) => {
      groupedRecords.push({
        ...item,
        srNo: srNo,
        isFirstInGroup: idx === 0,
        groupSize: items.length,
        perbibalance: perBISummary[biNumber] + adjustmentValue,
        adjustmentValue,
      });
    });
  });

  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const handleAdjustment = (biNumber) => {
    const biRecords = records.filter(r => r.biNumber === biNumber);

    const inbondRecord = biRecords.find(r => r.type === "Inbond");

    const inbondDuty = inbondRecord?.duty || 0;
    const exbondDuty =
      Math.abs(biRecords.find(r => r.type === "Exbond")?.duty || 0);

    const perBI = perBISummary[biNumber] || 0;
    const existingAdjustment = adjustmentMap[biNumber] || 0;

    setAdjustmentData({
      id: inbondRecord?.id, //THIS IS inbond_master_id
      biNumber: biNumber.replace(/^bi/i, "BI"),
      inbondDuty,
      exbondDuty,
      perBI,
      adjustmentAmount: existingAdjustment,
    });

    setShowAdjustmentModal(true);
  };

  //this api is called to create adjustments
  const createAdjustment = async () => {
    setLoading(true);

    try {
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DUTY_SPACE.CREATE_ADJUSTMENTS(adjustmentData.id)}`,
        {
          method: "POST",
          headers: getHeaders(true),
          body: JSON.stringify({
            adjustment_amount_inr: Number(adjustmentData.adjustmentAmount),
          }),
        }
      );

      // 🔐 token refresh
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          logout();
          return;
        }

        response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DUTY_SPACE.CREATE_ADJUSTMENTS(adjustmentData.id)}`,
          {
            method: "POST",
            headers: getHeaders(true),
            body: JSON.stringify({
              adjustment_amount_inr: Number(adjustmentData.adjustmentAmount),
            }),
          }
        );
      }

      const result = await response.json();

      if (result.status === 200) {
        // Refresh table data
        await fetchDutySpaceTotals();
        setShowAdjustmentModal(false);
      } else {
        console.error("Adjustment failed:", result.message);
      }
    } catch (error) {
      console.error("Create adjustment error:", error);
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <div className="min-h-screen p-2">
        {/* Summary Cards */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
          <div className="flex flex-1 sm:flex-none items-center gap-4 sm:gap-6 bg-white shadow-soft rounded-xl p-4">
            <div className="p-3 rounded-full bg-gray-100">
              <Calculator className="text-gray-700" size={22} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Current Balance Duty</p>
              <p className="text-[18px] sm:text-[20px] font-semibold text-gray-900">
                ₹{finalBalance.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className="flex flex-1 sm:flex-none items-center gap-4 sm:gap-6 bg-white shadow-soft rounded-xl p-4">
            <div className="p-3 rounded-full bg-[#dbf6e5]">
              <Repeat className="text-[#588d57]" size={22} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Total BI Adjustments</p>
              <p className="text-[18px] sm:text-[20px] font-semibold text-gray-900">
                ₹{totalAdjustments.toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="border-gray-200 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2 gap-2 sm:gap-0">
              <div className="flex gap-4 sm:gap-6 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-3 text-[13px] font-medium relative transition-all whitespace-nowrap
                    ${activeTab === tab.id ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}
                  `}
                  >
                    {tab.label}
                    <span
                      className={`ml-2 px-1.5 py-0.5 rounded text-[11px] font-semibold min-w-[24px] inline-block text-center
                      ${activeTab === tab.id ? getTabColor() : 'bg-gray-200 text-gray-700'}
                    `}
                    >
                      {tab.count}
                    </span>
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full"></div>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 pr-4 mt-2 sm:mt-0">
                <p className="text-sm text-gray-500 font-medium">Total Balance</p>
                <p className="text-sm font-semibold text-gray-900">
                  ₹{balanceSheetValue.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Sr No.</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">BI Number</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Type</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Duty (₹)</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Per BI Number (₹)</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Adjustment</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12"></th>
                </tr>
              </thead>

              <tbody className="bg-white text-xs">
                {groupedRecords.length === 0 ? (
                  <tr>
                    <td className="py-10 text-center" colSpan={7}>
                      <NoFound message="No duty records found" />
                    </td>
                  </tr>
                ) : (
                  groupedRecords.map((record, index) => {
                    const isHovered = hoveredBIGroup === record.biNumber;

                    return (
                      <tr
                        key={index}
                        className={`transition-colors ${isHovered ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'} border-b border-dashed border-gray-300`}
                        onMouseEnter={() => setHoveredBIGroup(record.biNumber)}
                        onMouseLeave={() => setHoveredBIGroup(null)}
                      >
                        {record.isFirstInGroup && (
                          <>
                            <td className="px-2 py-2 text-center" rowSpan={record.groupSize}>
                              {record.srNo}
                            </td>
                            <td className="px-2 py-2 text-center" rowSpan={record.groupSize}>
                              {record.biNumber.toUpperCase()}
                            </td>
                          </>
                        )}

                        <td className="px-2 py-2 text-center w-1/6">
                          <span className={`inline-block px-2 py-1 text-xs rounded font-semibold 
                            ${record.type === 'Inbond' ? 'bg-[#fff1d6] text-[#be7600]' : 'bg-[#dbf6e5] text-[#588d57]'}`}>
                            {record.type}
                          </span>
                        </td>

                        <td className="px-2 py-2 text-center w-1/6">{formatIndianNumber(Math.abs(record.duty))}</td>

                        {record.isFirstInGroup && (
                          <td className="px-2 py-2 text-center w-1/6" rowSpan={record.groupSize}>
                            {formatIndianNumber(record.perbibalance)}
                          </td>
                        )}

                        {record.isFirstInGroup && (
                          <td className="px-2 py-2 text-center w-1/12" rowSpan={record.groupSize}>
                            {formatIndianNumber(record.adjustmentValue)}
                          </td>
                        )}

                        {record.isFirstInGroup && (
                          <td className="px-3 py-2 text-center w-1/12" rowSpan={record.groupSize} onClick={(e) => e.stopPropagation()}>
                            <ActionDropdown 
                            onAdjustment={() => handleAdjustment(record.biNumber)} 
                            disabled={record.perbibalance === 0}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

          </div>
          {showAdjustmentModal && adjustmentData && (
            <Modal
              title="Duty Adjustment"
              onClose={() => setShowAdjustmentModal(false)}
              onSubmit={(e) => {
                e.preventDefault();
                createAdjustment();
              }}
              submitText="Save Adjustment"
            >
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <LabelInput
                    label="BI Number"
                    type="text"
                    value={adjustmentData.biNumber}
                    readonly
                  />
                </div>
                <div>
                  <LabelInput
                    label="Inbond Duty"
                    type="text"
                    value={adjustmentData.inbondDuty}
                    readonly
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <LabelInput
                    label="Per BI Number"
                    type="text"
                    value={adjustmentData.perBI}
                    readonly
                  />
                </div>
                <div>
                  <LabelInput
                    label="Exbond Duty"
                    type="text"
                    value={adjustmentData.exbondDuty}
                    readonly
                  />
                </div>
              </div>
              <div>
                <LabelInput
                  label="Adjustment Amount"
                  type="number"
                  value={adjustmentData.adjustmentAmount}
                  onChange={(e) =>
                    setAdjustmentData(prev => ({
                      ...prev,
                      adjustmentAmount: e.target.value,
                    }))
                  }
                  required
                />
              </div>
            </Modal>
          )}
        </div>
      </div>
    </>
  );
}
